import { useMemo, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { TabButton } from '../components/ui/Tabs/TabButton.js';
import { TabGroup } from '../components/ui/Tabs/TabGroup.js';
import {
  useOpsAssignments,
  useOpsAssignmentHistory,
  useSetOpsAssignment,
} from '../hooks/useOps.js';
import { useMemberList } from '../hooks/useMember.js';
import type { OpsAssignment, OpsCover, OpsRotationCadence } from '../types/ops.js';
import type { OpsAssignmentEdit } from '../lib/api/ops.js';
import {
  coverFor,
  formatMd,
  opsAssigneeCandidates,
  rotationInfo,
  type RotationInfo,
} from '@cortex/ops-core';
import { OpsHistoryPanel } from '../components/features/ops/OpsHistoryPanel.js';
import { UpcomingCoverChips } from '../components/features/ops/UpcomingCoverChips.js';
import { OpsAssignmentEditModal } from '../components/features/ops/OpsAssignmentEditModal.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 担当割当ボード（管理ビュー）。オペ担 と review_sql 当番はどちらも「ローテーション queue + cadence（日次/週次/月次で 1 人ずつ回る）」で表し、今日の担当を queue から導出する。その上に「日付指定のカバー（当日=シフト代理 / 将来日=休日オペたん）」を役割ごとに重ねられ、該当日はローテーションより優先される。ボードには今日の担当を coverFor で overlay して表示する。割当は Firestore に永続化し、担当者候補は /members のエンジニア一覧（nickName）から選ぶ
 * @graph-connects useOpsAssignments [calls] サービス別割当の取得
 * @graph-connects useSetOpsAssignment [calls] サービス別割当の更新（永続化）
 * @graph-connects useMemberList [calls] 担当者候補（エンジニア nickName）一覧の取得
 */
export const Route = createFileRoute('/ops/assignments')({
  component: OpsAssignmentsPage,
});

/** 1 サービス分の編集内容。 */
interface AssignmentEdit {
  /** オペ担ローテーション queue。 */
  opetanQueue: string[];
  /** オペ担の頻度。 */
  opetanCadence: OpsRotationCadence;
  /** review_sql 当番ローテーション queue。 */
  reviewerQueue: string[];
  /** review_sql の頻度。 */
  reviewerCadence: OpsRotationCadence;
  /** 日付指定のカバー（当日=シフト代理 / 将来日=休日オペたん）。 */
  covers: OpsCover[];
}

/** RotationCell の props。 */
interface RotationCellProps {
  /** 導出済みのローテーション情報。 */
  info: RotationInfo;
  /** この役割の頻度（next 表示に使う）。 */
  cadence: OpsRotationCadence;
  /** queue の並び（透明性のため表示）。 */
  queue: string[];
}

/** 今日の担当 + next + queue を表示する。 @graph-connects none */
function RotationCell({ info, cadence, queue }: RotationCellProps): ReactNode {
  const { t } = useTranslation();
  if (info.current === null) {
    return (
      <Badge variant="warning" size="sm">
        {t('ops.assignments.emptyQueue')}
      </Badge>
    );
  }
  return (
    <div>
      <Badge variant={info.overridden ? 'warning' : 'info'} size="sm">
        {info.current}
      </Badge>
      <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
        {info.overridden
          ? t('ops.assignments.rotation.shiftCover', { scheduled: info.scheduled ?? '—' })
          : info.next !== null
            ? t('ops.assignments.rotation.next', {
                next: info.next,
                start: info.nextStart !== null ? formatMd(info.nextStart) : '—',
                cadence,
              })
            : t('ops.assignments.rotation.fixed')}
      </div>
      <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
        {t('ops.assignments.rotation.queue', {
          queue: queue.length === 0 ? '—' : queue.join(' → '),
        })}
      </div>
    </div>
  );
}

/** @graph-connects none */
function OpsAssignmentsPage(): ReactNode {
  const { t } = useTranslation();
  const now = new Date();
  const { data } = useOpsAssignments();
  const setAssignment = useSetOpsAssignment();
  const { data: history } = useOpsAssignmentHistory();
  const rows = data ?? [];
  // 担当者候補は /members のエンジニア一覧（nickName）を単一 source にする（ハードコードしない）。
  const { data: memberList } = useMemberList();
  // 候補はエンジニア一覧 + 追加の担当者（ops-core。依頼詳細の担当ドロップダウンと同じ）。
  const engineerOptions = useMemo(
    () => opsAssigneeCandidates(memberList?.engineers ?? []).map((n) => ({ value: n, label: n })),
    [memberList],
  );
  const [editing, setEditing] = useState<OpsAssignment | null>(null);
  const [view, setView] = useState<'board' | 'history'>('board');

  const currentOf = (a: OpsAssignment): AssignmentEdit => ({
    opetanQueue: a.opetanQueue,
    opetanCadence: a.opetanCadence,
    reviewerQueue: a.reviewerQueue,
    reviewerCadence: a.reviewerCadence,
    covers: a.covers,
  });

  const saveAssignment = (service: OpsAssignment['service'], edit: OpsAssignmentEdit): void => {
    setAssignment.mutate(
      { service, edit },
      {
        onSuccess: () => {
          setEditing(null);
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      <TabGroup ariaLabel={t('ops.assignments.viewLabel')}>
        <TabButton
          active={view === 'board'}
          label={t('ops.assignments.board')}
          onClick={() => {
            setView('board');
          }}
        />
        <TabButton
          active={view === 'history'}
          label={t('ops.assignments.history')}
          onClick={() => {
            setView('history');
          }}
        />
      </TabGroup>

      {view === 'board' && (
        <>
          <p className="text-sm text-[var(--color-text-muted)]">
            <Trans
              i18nKey="ops.assignments.intro"
              components={[
                <span key="opetan" className="font-semibold" />,
                <span key="reviewer" className="font-semibold" />,
                <span key="cover" className="font-semibold" />,
              ]}
            />
          </p>
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <thead>
                  <tr className="text-left text-xs text-[var(--color-text-muted)]">
                    <th className="px-4 py-2 font-medium">
                      {t('ops.assignments.columns.service')}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t('ops.assignments.columns.opetanToday')}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t('ops.assignments.columns.reviewerToday')}
                    </th>
                    <th
                      className="px-2 py-2 font-medium"
                      aria-label={t('ops.assignments.columns.actions')}
                    />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => {
                    const cur = currentOf(a);
                    const opetanInfo = rotationInfo(
                      cur.opetanQueue,
                      cur.opetanCadence,
                      coverFor(cur.covers, 'opetan', now),
                      now,
                    );
                    const reviewerInfo = rotationInfo(
                      cur.reviewerQueue,
                      cur.reviewerCadence,
                      coverFor(cur.covers, 'reviewer', now),
                      now,
                    );
                    return (
                      <tr key={a.service} className="border-t border-[var(--color-border)]">
                        <td className="px-4 py-3 align-top font-semibold text-[var(--color-text-primary)]">
                          {a.serviceLabel}
                          <UpcomingCoverChips covers={a.covers} now={now} />
                        </td>
                        <td className="px-2 py-3">
                          <RotationCell
                            info={opetanInfo}
                            cadence={cur.opetanCadence}
                            queue={cur.opetanQueue}
                          />
                        </td>
                        <td className="px-2 py-3">
                          <RotationCell
                            info={reviewerInfo}
                            cadence={cur.reviewerCadence}
                            queue={cur.reviewerQueue}
                          />
                        </td>
                        <td className="px-2 py-3 text-right align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditing(a);
                            }}
                          >
                            {t('ops.assignments.edit')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {view === 'history' && <OpsHistoryPanel events={history ?? []} />}

      <OpsAssignmentEditModal
        assignment={editing}
        engineerOptions={engineerOptions}
        now={now}
        isSaving={setAssignment.isPending}
        saveFailed={setAssignment.isError}
        onClose={() => {
          setEditing(null);
        }}
        onSave={saveAssignment}
      />
    </div>
  );
}
